// ============================================================================
// CLEO'S SECRET — RetentionPage
// File: src/pages/RetentionPage.jsx
//
// CLINICIAN OPERATIONAL TOOL (not patient-facing). The workstation where
// Robyn reviews retention triggers and sends outreach emails.
//
// Pattern: obsidian header strip (workstation chrome) + cream main canvas
// with two panes — trigger list (left) + detail editor (right).
//
// This is the same palette and typography as the rest of the platform,
// but with denser layouts, no closing flourishes, no editorial framing.
// Tools should feel calmly beautiful, not ceremonial.
//
// Six API calls preserved:
//   • /api/get-retention-triggers (load)
//   • /api/generate-retention-triggers (refresh)
//   • /api/action-retention-trigger (update_message / mark_actioned / dismiss)
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  FF, FS, P, T,
  Spinner,
} from '../lib/design-tokens';
import RequireAdmin from '../components/RequireAdmin';

// ───────── Helpers ─────────
function formatDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function timeAgo(s) {
  if (!s) return '';
  var diff = Date.now() - new Date(s).getTime();
  var hours = Math.floor(diff / (1000 * 60 * 60));
  var days = Math.floor(hours / 24);
  if (hours < 1) return 'just now';
  if (hours < 24) return hours + 'h ago';
  if (days === 1) return 'yesterday';
  return days + ' days ago';
}

// ───────── Urgency → status palette ─────────
function urgencyColor(u) {
  if (u === 'high') return P.RED;
  if (u === 'medium') return P.AMBER;
  return P.GREEN;
}
function urgencyBg(u) {
  if (u === 'high') return P.RED_BG;
  if (u === 'medium') return P.AMBER_BG;
  return P.GREEN_BG;
}
function urgencyLabel(u) {
  if (u === 'high') return 'High';
  if (u === 'medium') return 'Medium';
  return 'Low';
}

// ───────── Trigger type → human label ─────────
function triggerTypeLabel(t) {
  var labels = {
    lapsed_patient: 'Lapsed patient',
    low_score_no_followup: 'Low score, no follow-up',
    pathology_no_followup: 'Pathology not discussed',
    intake_no_booking: 'Intake without booking',
  };
  return labels[t] || t;
}

// ───────── Patient display name ─────────
function patientName(patient) {
  if (!patient) return 'Unknown patient';
  var first = patient.first_name || '';
  var last = patient.last_name || '';
  var full = (first + ' ' + last).trim();
  return full || patient.email || 'Unknown patient';
}

// ───────── Section label component (compact, dashboard-style) ─────────
function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: FF,
      fontSize: 'clamp(11px, 0.95vw, 13px)',
      fontWeight: 700,
      letterSpacing: '0.32em',
      textTransform: 'uppercase',
      color: P.BR2,
      marginBottom: 'clamp(14px, 1.6vw, 18px)',
    }}>{children}</div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// RetentionPage — outer wrapper. Gates on patients.is_admin via RequireAdmin
// so the inner page (and its data-fetching useEffects) only mount when the
// caller is confirmed admin. See src/components/RequireAdmin.jsx.
// ════════════════════════════════════════════════════════════════════════════
export default function RetentionPage(props) {
  return (
    <RequireAdmin email={props.email} isAdmin={props.isAdmin} onNavigate={props.onNavigate}>
      <RetentionPageInner onNavigate={props.onNavigate} />
    </RequireAdmin>
  );
}

function RetentionPageInner(props) {
  var onNavigate = props.onNavigate;

  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var refreshingState = useState(false);
  var refreshing = refreshingState[0];
  var setRefreshing = refreshingState[1];

  var selectedState = useState(null);
  var selected = selectedState[0];
  var setSelected = selectedState[1];

  var editedSubjectState = useState('');
  var editedSubject = editedSubjectState[0];
  var setEditedSubject = editedSubjectState[1];

  var editedMessageState = useState('');
  var editedMessage = editedMessageState[0];
  var setEditedMessage = editedMessageState[1];

  var copiedState = useState(false);
  var copied = copiedState[0];
  var setCopied = copiedState[1];

  function loadData() {
    setLoading(true);
    fetch('/api/get-retention-triggers', { method: 'POST' })
      .then(function(res) { return res.json(); })
      .then(function(result) { setData(result); setLoading(false); })
      .catch(function() { setLoading(false); });
  }

  useEffect(function() { loadData(); }, []);

  function refresh() {
    setRefreshing(true);
    fetch('/api/generate-retention-triggers', { method: 'POST' })
      .then(function(res) { return res.json(); })
      .then(function() { loadData(); setRefreshing(false); })
      .catch(function() { setRefreshing(false); });
  }

  function selectTrigger(trigger) {
    setSelected(trigger);
    setEditedSubject(trigger.outreach_subject || '');
    setEditedMessage(trigger.outreach_message || '');
    setCopied(false);
  }

  function saveEdits() {
    if (!selected) return;
    fetch('/api/action-retention-trigger', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        triggerId: selected.id,
        action: 'update_message',
        subject: editedSubject,
        message: editedMessage,
      }),
    })
      .then(function() { loadData(); })
      .catch(function() {});
  }

  function markActioned() {
    if (!selected) return;
    fetch('/api/action-retention-trigger', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        triggerId: selected.id,
        action: 'mark_actioned',
        actionedBy: 'robyn',
      }),
    })
      .then(function() { setSelected(null); loadData(); });
  }

  function dismiss(reason) {
    if (!selected) return;
    fetch('/api/action-retention-trigger', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        triggerId: selected.id,
        action: 'dismiss',
        reason: reason,
        actionedBy: 'robyn',
      }),
    })
      .then(function() { setSelected(null); loadData(); });
  }

  function copyMessage() {
    var fullMessage = 'Subject: ' + editedSubject + '\n\n' + editedMessage;
    var textarea = document.createElement('textarea');
    textarea.value = fullMessage;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    setCopied(true);
    setTimeout(function() { setCopied(false); }, 2000);
  }

  // ────────────────────────────────────────────────────────────────────────
  // LOADING STATE
  // ────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        background: P.OBS,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Spinner color={P.GO2} size={48} />
      </div>
    );
  }

  var triggers = (data && data.triggers) || [];
  var stats = (data && data.stats) || { total: 0, high: 0, medium: 0, low: 0 };

  // ────────────────────────────────────────────────────────────────────────
  // STAT CARD (single inline component)
  // ────────────────────────────────────────────────────────────────────────
  function StatCard({ label, value, highlight, color }) {
    return (
      <div style={{
        flex: 1,
        minWidth: 140,
        padding: 'clamp(18px, 2vw, 24px)',
        background: highlight ? 'rgba(201, 169, 97, 0.08)' : P.PAP,
        border: '1px solid ' + (highlight ? P.GO : P.SAND3),
        borderTop: '2px solid ' + (highlight ? P.GO : P.SAND3),
      }}>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(10px, 0.85vw, 12px)',
          fontWeight: 700,
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: P.BR2,
          marginBottom: 10,
        }}>{label}</div>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(32px, 3.4vw, 44px)',
          fontWeight: 400,
          lineHeight: 1,
          letterSpacing: '-0.015em',
          color: color || P.INK,
        }}>{value}</div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // TRIGGER CARD (list item, left pane)
  // ────────────────────────────────────────────────────────────────────────
  function TriggerCard({ trigger }) {
    var patient = trigger.patients;
    var isSelected = selected && selected.id === trigger.id;
    return (
      <div
        onClick={function() { selectTrigger(trigger); }}
        style={{
          padding: 'clamp(16px, 1.6vw, 20px)',
          background: isSelected ? 'rgba(201, 169, 97, 0.08)' : P.PAP,
          border: '1px solid ' + (isSelected ? P.GO : P.SAND3),
          borderLeft: '3px solid ' + urgencyColor(trigger.urgency),
          cursor: 'pointer',
          marginBottom: 10,
          transition: 'background 0.2s, border-color 0.2s',
        }}
        onMouseEnter={function(e) {
          if (!isSelected) e.currentTarget.style.background = 'rgba(232,221,196,0.5)';
        }}
        onMouseLeave={function(e) {
          if (!isSelected) e.currentTarget.style.background = P.PAP;
        }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          marginBottom: 8,
        }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(15px, 1.3vw, 17px)',
            fontWeight: 500,
            color: P.INK,
            lineHeight: 1.2,
            flex: 1,
            minWidth: 0,
            letterSpacing: '-0.005em',
          }}>{patientName(patient)}</div>
          <span style={{
            fontFamily: FF,
            fontSize: 'clamp(9px, 0.75vw, 10px)',
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: urgencyColor(trigger.urgency),
            background: urgencyBg(trigger.urgency),
            border: '1px solid ' + urgencyColor(trigger.urgency),
            padding: '4px 8px',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>{urgencyLabel(trigger.urgency)}</span>
        </div>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(10px, 0.85vw, 11px)',
          fontWeight: 600,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: P.BR2,
          marginBottom: 8,
        }}>{triggerTypeLabel(trigger.trigger_type)}</div>
        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(13px, 1.1vw, 14px)',
          color: P.TL,
          lineHeight: 1.55,
          margin: 0,
          marginBottom: 8,
        }}>{trigger.trigger_reason}</p>
        <div style={{
          fontFamily: FS,
          fontSize: 'clamp(10px, 0.85vw, 11px)',
          color: P.TF,
          fontStyle: 'italic',
          letterSpacing: '0.02em',
        }}>Generated {timeAgo(trigger.generated_at)}</div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // DETAIL PANE (right side, when a trigger is selected)
  // ────────────────────────────────────────────────────────────────────────
  function renderDetail() {
    if (!selected) {
      return (
        <div style={{
          padding: 'clamp(60px, 8vw, 100px) clamp(24px, 3vw, 40px)',
          textAlign: 'center',
          background: 'rgba(232,221,196,0.3)',
          border: '1px dashed ' + P.SAND3,
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <p style={{
            ...T.premise,
            fontSize: 'clamp(18px, 1.8vw, 24px)',
            color: P.BR2,
            maxWidth: 380,
            margin: 0,
          }}>Select a patient to review their outreach message.</p>
        </div>
      );
    }

    var patient = selected.patients;
    var patientEmail = patient && patient.email;
    var patientPhone = patient && patient.phone;

    return (
      <div>
        {/* Patient header */}
        <div style={{ marginBottom: 'clamp(20px, 2.2vw, 28px)' }}>
          <h2 style={{
            fontFamily: FF,
            fontSize: 'clamp(24px, 2.4vw, 32px)',
            fontWeight: 400,
            color: P.INK,
            lineHeight: 1.15,
            letterSpacing: '-0.012em',
            margin: '0 0 6px',
          }}>{patientName(patient)}</h2>
          <div style={{
            fontFamily: FS,
            fontSize: 'clamp(12px, 1vw, 14px)',
            color: P.TF,
            letterSpacing: '0.02em',
          }}>
            {patientEmail || ''}
            {patientPhone ? ' \u00b7 ' + patientPhone : ''}
          </div>
        </div>

        {/* Trigger reason callout */}
        <div style={{
          padding: 'clamp(18px, 2vw, 24px)',
          background: urgencyBg(selected.urgency),
          border: '1px solid ' + urgencyColor(selected.urgency),
          borderLeft: '3px solid ' + urgencyColor(selected.urgency),
          marginBottom: 'clamp(24px, 2.6vw, 32px)',
        }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(10px, 0.85vw, 12px)',
            fontWeight: 700,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: urgencyColor(selected.urgency),
            marginBottom: 10,
          }}>{triggerTypeLabel(selected.trigger_type)}</div>
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(14px, 1.2vw, 16px)',
            color: P.TX,
            lineHeight: 1.65,
            margin: 0,
          }}>{selected.trigger_reason}</p>
        </div>

        {/* Outreach editor */}
        <SectionLabel>Outreach email</SectionLabel>

        {/* Subject field */}
        <div style={{ marginBottom: 'clamp(14px, 1.6vw, 18px)' }}>
          <label style={{
            display: 'block',
            fontFamily: FF,
            fontSize: 'clamp(10px, 0.85vw, 11px)',
            fontWeight: 700,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 8,
          }}>Subject</label>
          <input
            type="text"
            value={editedSubject}
            onChange={function(e) { setEditedSubject(e.target.value); }}
            style={{
              width: '100%',
              padding: '14px 16px',
              fontFamily: FF,
              fontSize: 'clamp(14px, 1.2vw, 16px)',
              color: P.INK,
              background: P.PAP,
              border: '1px solid ' + P.SAND3,
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s',
            }}
            onFocus={function(e) { e.currentTarget.style.borderColor = P.BR; }}
            onBlur={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}
          />
        </div>

        {/* Message field */}
        <div style={{ marginBottom: 'clamp(20px, 2.2vw, 28px)' }}>
          <label style={{
            display: 'block',
            fontFamily: FF,
            fontSize: 'clamp(10px, 0.85vw, 11px)',
            fontWeight: 700,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 8,
          }}>Message</label>
          <textarea
            value={editedMessage}
            onChange={function(e) { setEditedMessage(e.target.value); }}
            rows={14}
            style={{
              width: '100%',
              padding: '16px 18px',
              fontFamily: FF,
              fontSize: 'clamp(14px, 1.2vw, 16px)',
              color: P.TX,
              background: P.PAP,
              border: '1px solid ' + P.SAND3,
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
              lineHeight: 1.75,
              transition: 'border-color 0.2s',
            }}
            onFocus={function(e) { e.currentTarget.style.borderColor = P.BR; }}
            onBlur={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}
          />
        </div>

        {/* Edit / Copy row */}
        <div style={{
          display: 'flex',
          gap: 10,
          marginBottom: 'clamp(16px, 1.8vw, 20px)',
          flexWrap: 'wrap',
        }}>
          <button
            onClick={saveEdits}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '14px 24px',
              background: 'transparent',
              color: P.BR2,
              border: '1px solid ' + P.GO,
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(201, 169, 97, 0.08)'; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}>
            Save edits
          </button>
          <button
            onClick={copyMessage}
            style={{
              flex: 1,
              minWidth: 200,
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '14px 24px',
              background: copied ? P.GREEN : P.INK,
              color: P.PAP,
              border: 'none',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}>
            {copied ? 'Copied' : 'Copy to clipboard'}
          </button>
        </div>

        {/* Workflow note */}
        <div style={{
          padding: 'clamp(14px, 1.6vw, 18px) clamp(16px, 1.8vw, 22px)',
          background: 'rgba(201, 169, 97, 0.06)',
          borderLeft: '3px solid ' + P.GO,
          marginBottom: 'clamp(20px, 2.2vw, 28px)',
        }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(10px, 0.85vw, 11px)',
            fontWeight: 700,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 8,
          }}>Workflow</div>
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(13px, 1.1vw, 14px)',
            color: P.TL,
            lineHeight: 1.65,
            margin: 0,
          }}>Copy the message, paste it into your email client, send to the patient. Then mark this trigger as sent.</p>
        </div>

        {/* Dismiss / Mark sent row */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            onClick={function() { dismiss('not_appropriate'); }}
            style={{
              flex: 1,
              minWidth: 140,
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '14px 24px',
              background: 'transparent',
              color: P.RED,
              border: '1px solid ' + P.RED,
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={function(e) { e.currentTarget.style.background = P.RED_BG; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}>
            Dismiss
          </button>
          <button
            onClick={markActioned}
            style={{
              flex: 2,
              minWidth: 200,
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '14px 24px',
              background: P.GREEN,
              color: P.PAP,
              border: 'none',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={function(e) { e.currentTarget.style.opacity = 0.9; }}
            onMouseLeave={function(e) { e.currentTarget.style.opacity = 1; }}>
            Mark as sent
          </button>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // PAGE LAYOUT
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ background: P.PAP, minHeight: '100vh', fontFamily: FF }}>

      {/* ═════════ Obsidian workstation header ═════════ */}
      <header style={{
        background: P.OBS,
        borderBottom: '1px solid ' + P.GO,
        padding: 'clamp(20px, 2.4vw, 32px) clamp(24px, 3vw, 40px)',
      }}>
        <div style={{
          maxWidth: 1400,
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(10px, 0.85vw, 12px)',
              fontWeight: 700,
              letterSpacing: '0.36em',
              textTransform: 'uppercase',
              color: P.GO2,
              marginBottom: 6,
            }}>Cleo's Secret &middot; Workstation</div>
            <h1 style={{
              fontFamily: FF,
              fontSize: 'clamp(22px, 2.4vw, 32px)',
              fontWeight: 400,
              color: P.PAP,
              lineHeight: 1.15,
              letterSpacing: '-0.008em',
              margin: 0,
            }}>Patient <span style={{ fontStyle: 'italic', color: P.GO2 }}>outreach.</span></h1>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={refresh}
              disabled={refreshing}
              style={{
                fontFamily: FS,
                fontSize: 'clamp(11px, 0.9vw, 12px)',
                fontWeight: 600,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                padding: '12px 22px',
                background: P.GO,
                color: P.OBS,
                border: 'none',
                cursor: refreshing ? 'default' : 'pointer',
                opacity: refreshing ? 0.5 : 1,
                transition: 'background 0.2s',
              }}
              onMouseEnter={function(e) { if (!refreshing) e.currentTarget.style.background = P.GO2; }}
              onMouseLeave={function(e) { if (!refreshing) e.currentTarget.style.background = P.GO; }}>
              {refreshing ? 'Refreshing\u2026' : 'Refresh triggers'}
            </button>
            <button
              onClick={function() { if (onNavigate) onNavigate('clinical'); }}
              style={{
                fontFamily: FS,
                fontSize: 'clamp(11px, 0.9vw, 12px)',
                fontWeight: 600,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                padding: '12px 22px',
                background: 'transparent',
                color: P.GO2,
                border: '1px solid ' + P.GO2,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(224, 198, 133, 0.1)'; }}
              onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}>
              Dashboard
            </button>
          </div>
        </div>
      </header>

      {/* ═════════ Main canvas ═════════ */}
      <main style={{
        maxWidth: 1400,
        margin: '0 auto',
        padding: 'clamp(28px, 3.4vw, 48px) clamp(24px, 3vw, 40px) clamp(48px, 6vw, 80px)',
      }}>

        {/* Stats strip */}
        <div style={{
          display: 'flex',
          gap: 12,
          marginBottom: 'clamp(28px, 3.4vw, 40px)',
          flexWrap: 'wrap',
        }}>
          <StatCard label="Total" value={stats.total} />
          <StatCard
            label="High urgency"
            value={stats.high}
            highlight={stats.high > 0}
            color={stats.high > 0 ? P.RED : P.INK} />
          <StatCard label="Medium" value={stats.medium} color={stats.medium > 0 ? P.AMBER : P.INK} />
          <StatCard label="Low" value={stats.low} />
        </div>

        {/* Empty state OR two-pane workspace */}
        {stats.total === 0 ? (
          <div style={{
            padding: 'clamp(48px, 6vw, 80px) clamp(24px, 3vw, 40px)',
            textAlign: 'center',
            background: 'rgba(232,221,196,0.4)',
            border: '1px dashed ' + P.SAND3,
          }}>
            <p style={{
              ...T.premise,
              fontSize: 'clamp(20px, 2vw, 28px)',
              color: P.BR2,
              marginBottom: 16,
            }}>No outreach needed right now.</p>
            <p style={{
              ...T.bodyMd,
              color: P.TL,
              maxWidth: 520,
              margin: '0 auto clamp(28px, 3vw, 40px)',
              lineHeight: 1.75,
            }}>
              Either no patients have met the trigger criteria, or every current trigger has been actioned. Check again when you are ready.
            </p>
            <button
              onClick={refresh}
              disabled={refreshing}
              style={{
                fontFamily: FS,
                fontSize: 'clamp(11px, 0.95vw, 12px)',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                padding: '14px 32px',
                background: P.INK,
                color: P.PAP,
                border: 'none',
                cursor: refreshing ? 'default' : 'pointer',
                opacity: refreshing ? 0.5 : 1,
                transition: 'background 0.2s',
              }}
              onMouseEnter={function(e) { if (!refreshing) e.currentTarget.style.background = P.BR; }}
              onMouseLeave={function(e) { if (!refreshing) e.currentTarget.style.background = P.INK; }}>
              {refreshing ? 'Checking\u2026' : 'Check for new triggers'}
            </button>
          </div>
        ) : (
          <div className="cs-retention-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(320px, 400px) 1fr',
            gap: 'clamp(24px, 2.6vw, 36px)',
            alignItems: 'start',
          }}>

            {/* Left pane: trigger list */}
            <div>
              <SectionLabel>Patients to contact &middot; {triggers.length}</SectionLabel>
              {triggers.map(function(t) { return <TriggerCard key={t.id} trigger={t} />; })}
            </div>

            {/* Right pane: detail editor */}
            <div style={{
              position: 'sticky',
              top: 24,
              maxHeight: 'calc(100vh - 48px)',
              overflowY: 'auto',
              paddingLeft: 'clamp(20px, 2.2vw, 28px)',
              borderLeft: '1px solid ' + P.SAND3,
            }} className="cs-retention-detail">
              {renderDetail()}
            </div>

          </div>
        )}

      </main>

      {/* Responsive: stack panes below 900px */}
      <style>{`
        @media (max-width: 900px) {
          .cs-retention-grid {
            grid-template-columns: 1fr !important;
          }
          .cs-retention-detail {
            position: static !important;
            border-left: none !important;
            border-top: 1px solid ${P.SAND3};
            padding-left: 0 !important;
            padding-top: clamp(20px, 2.4vw, 28px);
            max-height: none !important;
          }
        }
      `}</style>
    </div>
  );
}
